import type { Clause, ClauseOp, PatternBased, RuleBased } from '@kkp-fraud/shared';
import {
  kindOf,
  listToInput,
  opsFor,
  parseListInput,
  valueInputKind,
  type FieldName,
  type ValueInputKind,
} from '../fields';
import { t } from '../i18n';
import { isLongWindow, windowUnitFor } from '../longWindows';

/**
 * Rule document ⇄ Blockly workspace state, as plain JSON.
 *
 * NOTHING in this module imports Blockly. The block view is a second editor over the same
 * `RuleBased` / `PatternBased` object, and the only thing the two sides exchange is the
 * serialization format `Blockly.serialization.workspaces.{save,load}` speaks. Keeping the
 * conversion pure buys three things: jsdom can test the round trip without rendering an
 * SVG, the Sentence view pays nothing for the Blocks view, and a bug here is reproducible
 * from a JSON literal.
 *
 * ── What the blocks can express ────────────────────────────────────────────────
 * `packages/shared/src/index.ts:156-163` defines `Clause` as `{logic?, field, op, value}`
 * and `RuleBased.clauses` (:181) / `PatternBased.filters` (:194) as FLAT arrays of them.
 * There is no grouping node, so `(A OR B) AND C` cannot be persisted. The mockup's
 * `logic_op` block (`docs/console-mockup/blocks.js`) lets an analyst build exactly that
 * shape and would have to flatten it — silently changing the rule's meaning — on the way
 * back out. So the palette here is a STATEMENT STACK, not an expression tree: each
 * condition snaps under the previous one and carries its own AND/OR, one block per clause,
 * and nesting is not offered. Restoring nesting is a `packages/shared` change first.
 */

// =============================================================================
// The serialization shapes we produce and consume
// =============================================================================

export type BlockFieldValue = string | number;

export interface BlockState {
  type: string;
  id?: string;
  x?: number;
  y?: number;
  fields?: Record<string, BlockFieldValue>;
  inputs?: Record<string, { block: BlockState }>;
  next?: { block: BlockState };
}

export interface WorkspaceState {
  blocks?: { languageVersion: number; blocks: BlockState[] };
}

export type RuleKindShort = 'rb' | 'pb';

/** The hat block of each family — the only top block that carries a rule. */
export const HAT_TYPE: Record<RuleKindShort, string> = { rb: 'rb_when', pb: 'pb_when' };

/** RB conditions carry a LOGIC join; PB filters are always ANDed by the engine and do not. */
export const COND_PREFIX: Record<RuleKindShort, string> = { rb: 'rb_cond', pb: 'pb_filter' };

/**
 * One block type per value editor shape — the same six shapes `valueInputKind` returns. `set`
 * (R7, `in_set` / `not_in_set`) carries the Master list id as TEXT, not as a dropdown: Blockly
 * silently resets a dropdown whose stored value is not among its options, and a list the
 * console cannot see (another environment's, or one not loaded yet) would be rewritten on
 * load. Typed text round-trips exactly; the problem list flags an id that is not a list.
 */
const SUFFIX_OF: Record<ValueInputKind, string> = {
  number: 'num',
  pair: 'range',
  text: 'str',
  list: 'list',
  boolean: 'bool',
  set: 'set',
};

export const INPUT_KIND_OF_SUFFIX: Record<string, ValueInputKind> = {
  num: 'number',
  range: 'pair',
  str: 'text',
  list: 'list',
  bool: 'boolean',
  set: 'set',
};

export function condTypeFor(kind: RuleKindShort, field: FieldName, op: ClauseOp): string {
  return `${COND_PREFIX[kind]}_${SUFFIX_OF[valueInputKind(field, op)]}`;
}

/** Every condition block type, both families — what `defs.ts` defines and the toolbox offers. */
export const COND_TYPES: string[] = (['rb', 'pb'] as RuleKindShort[]).flatMap((k) =>
  Object.keys(INPUT_KIND_OF_SUFFIX).map((s) => `${COND_PREFIX[k]}_${s}`),
);

/** `rb_cond_list` → `list`. Returns null for anything that is not one of ours. */
export function suffixOf(type: string): string | null {
  const m = /^(?:rb_cond|pb_filter)_(num|range|str|list|bool|set)$/.exec(type);
  return m ? (m[1] as string) : null;
}

// =============================================================================
// The time unit of a pattern window
// =============================================================================

/**
 * 900 → {win: 15, unit: 60}. Always exact, so the round trip cannot drift. A long window (Wave 7,
 * over 24 h) in the unit the Sentence view opens it in: 30 days → 1 month, 7 days → 7 days,
 * 365 days → 1 year; one that is not whole days stays in seconds.
 */
export function splitWindow(seconds: number): { win: number; unit: number } {
  const s = Number.isFinite(seconds) ? seconds : 0;
  if (isLongWindow(s)) {
    const unit = windowUnitFor(s);
    return { win: s / unit, unit };
  }
  if (s > 0 && s % 3600 === 0) return { win: s / 3600, unit: 3600 };
  if (s > 0 && s % 60 === 0) return { win: s / 60, unit: 60 };
  return { win: s, unit: 1 };
}

// =============================================================================
// Rule → workspace state
// =============================================================================

function clauseBlock(kind: RuleKindShort, clause: Clause, index: number): BlockState {
  const field = clause.field as FieldName;
  const suffix = SUFFIX_OF[valueInputKind(field, clause.op)];
  const fields: Record<string, BlockFieldValue> = { FIELD: String(field) };
  // The first clause's join is ignored by the engine (`Clause.logic`, shared:159), so the
  // block shows the document's own default rather than inventing one.
  if (kind === 'rb') fields.LOGIC = index === 0 ? 'AND' : ((clause.logic ?? 'AND').toUpperCase() as string);

  if (suffix === 'bool') {
    fields.OP = clause.op;
    fields.VAL = clause.value === true ? 'true' : 'false';
  } else if (suffix === 'range') {
    const pair = Array.isArray(clause.value) ? clause.value : [0, 0];
    fields.MIN = typeof pair[0] === 'number' ? pair[0] : 0;
    fields.MAX = typeof pair[1] === 'number' ? pair[1] : 0;
  } else if (suffix === 'list') {
    fields.OP = clause.op;
    fields.VAL = listToInput(clause.value);
  } else if (suffix === 'set') {
    fields.OP = clause.op;
    fields.VAL = typeof clause.value === 'string' ? clause.value : '';
  } else if (suffix === 'num') {
    fields.OP = clause.op;
    fields.VAL = typeof clause.value === 'number' ? clause.value : Number(clause.value) || 0;
  } else {
    fields.OP = clause.op;
    fields.VAL = typeof clause.value === 'string' ? clause.value : String(clause.value ?? '');
  }

  return { type: `${COND_PREFIX[kind]}_${suffix}`, fields };
}

/** The clause list as one vertical stack — clause order is stack order, top to bottom. */
function condStack(kind: RuleKindShort, clauses: readonly Clause[]): BlockState | null {
  let head: BlockState | null = null;
  for (let i = clauses.length - 1; i >= 0; i--) {
    const block = clauseBlock(kind, clauses[i] as Clause, i);
    if (head) block.next = { block: head };
    head = block;
  }
  return head;
}

export function ruleToWorkspaceState(rule: RuleBased | PatternBased, kind: RuleKindShort): WorkspaceState {
  const top: BlockState =
    kind === 'pb'
      ? (() => {
          const pb = rule as PatternBased;
          const { win, unit } = splitWindow(pb.windowSeconds);
          return {
            type: 'pb_when',
            x: 16,
            y: 16,
            fields: {
              SCOPE: pb.scope ?? 'card_id',
              EVENT: pb.event ?? 'approved',
              AGG: pb.aggregate ?? 'count',
              AGGFIELD: pb.aggregate === 'count' ? '' : String(pb.aggregateField ?? ''),
              THR: Number.isFinite(pb.threshold) ? pb.threshold : 0,
              // UNIT before WIN: Blockly loads fields in this order, and picking a unit resets the number
              UNIT: String(unit),
              WIN: String(win),
              ACTION: pb.action,
            },
          } satisfies BlockState;
        })()
      : { type: 'rb_when', x: 16, y: 16, fields: { ACTION: rule.action } };

  const clauses = kind === 'pb' ? ((rule as PatternBased).filters ?? []) : ((rule as RuleBased).clauses ?? []);
  const stack = condStack(kind, clauses);
  if (stack) top.inputs = { COND: { block: stack } };

  return { blocks: { languageVersion: 0, blocks: [top] } };
}

// =============================================================================
// Workspace state → rule
// =============================================================================

export interface BlockConversion<R> {
  /** The rule the blocks describe, or `base` untouched when the workspace cannot be read. */
  rule: R;
  /** True when the blocks were readable — a false here means `rule` is `base`, unchanged. */
  ok: boolean;
  /** Everything the analyst has to see. Non-empty does not always mean `ok === false`. */
  warnings: string[];
}

function stackOf(head: BlockState | undefined): BlockState[] {
  const out: BlockState[] = [];
  let cur = head;
  while (cur) {
    out.push(cur);
    cur = cur.next?.block;
  }
  return out;
}

function str(block: BlockState, name: string): string {
  const v = block.fields?.[name];
  return v === undefined || v === null ? '' : String(v);
}

function num(block: BlockState, name: string): number {
  const v = block.fields?.[name];
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : Number.NaN;
}

/**
 * One block → one clause, or an error string.
 *
 * Every FIELD is checked against the closed catalogue and every OP against `opsFor` for
 * that field's kind — the same rules `ClauseBuilder` enforces with its dropdowns. A block
 * whose dropdown silently fell back to its first option (Blockly's behaviour when a stored
 * value is not in the option list) is caught here instead of being written into the draft.
 */
function clauseOf(block: BlockState, index: number, withLogic: boolean): { clause: Clause } | { error: string } {
  const suffix = suffixOf(block.type);
  const n = index + 1;
  if (!suffix) return { error: t('blocks.problem.notCondition', { n, type: block.type }) };

  const name = str(block, 'FIELD');
  if (!name) return { error: t('blocks.problem.noField', { n }) };
  // Wave 6: a standard field, else a custom field of the set (`kindOf`).
  const kind = kindOf(name);
  if (kind === undefined) return { error: t('blocks.problem.unknownField', { n, field: name }) };
  const field = name as FieldName;

  const op = (suffix === 'range' ? 'between' : (str(block, 'OP') as ClauseOp)) as ClauseOp;
  if (!op) return { error: t('blocks.problem.noOp', { n, field: name }) };
  if (!opsFor(kind).includes(op)) return { error: t('blocks.problem.badOp', { n, field: name, kind: t(`fieldkind.${kind}.label`), op }) };
  if (INPUT_KIND_OF_SUFFIX[suffix] !== valueInputKind(field, op)) {
    return { error: t('blocks.problem.wrongBlock', { n, field: name, op }) };
  }

  let value: unknown;
  if (suffix === 'bool') {
    value = str(block, 'VAL') === 'true';
  } else if (suffix === 'range') {
    const min = num(block, 'MIN');
    const max = num(block, 'MAX');
    if (!Number.isFinite(min) || !Number.isFinite(max)) return { error: t('blocks.problem.rangePair', { n, field: name }) };
    value = [min, max];
  } else if (suffix === 'list') {
    value = parseListInput(str(block, 'VAL'), kind);
  } else if (suffix === 'set') {
    // The Master list id as typed, trimmed; an empty or unknown one is the problem list's to report.
    value = str(block, 'VAL').trim();
  } else if (suffix === 'num') {
    const v = num(block, 'VAL');
    if (!Number.isFinite(v)) return { error: t('blocks.problem.notNumber', { n, field: name }) };
    value = v;
  } else {
    value = str(block, 'VAL');
  }

  const clause: Clause = { field, op, value };
  // Only rows after the first carry a join — the engine ignores the first one, and writing
  // it would show up as a phantom change in the release diff.
  if (withLogic && index > 0) {
    const logic = str(block, 'LOGIC').toUpperCase();
    return { clause: { logic: logic === 'OR' ? 'OR' : 'AND', ...clause } };
  }
  return { clause };
}

function readClauses(hat: BlockState, kind: RuleKindShort): { clauses: Clause[] } | { error: string } {
  const blocks = stackOf(hat.inputs?.COND?.block);
  const clauses: Clause[] = [];
  for (let i = 0; i < blocks.length; i++) {
    const got = clauseOf(blocks[i] as BlockState, i, kind === 'rb');
    if ('error' in got) return got;
    clauses.push(got.clause);
  }
  return { clauses };
}

export function workspaceStateToRule<R extends RuleBased | PatternBased>(
  state: WorkspaceState,
  base: R,
  kind: RuleKindShort,
): BlockConversion<R> {
  const tops = state.blocks?.blocks ?? [];
  const hats = tops.filter((b) => b.type === HAT_TYPE[kind]);
  const hat = hats[0];
  if (!hat) {
    return { rule: base, ok: false, warnings: [t('blocks.problem.noHead')] };
  }

  const warnings: string[] = [];
  if (hats.length > 1) warnings.push(t('blocks.problem.manyHeads', { n: hats.length }));
  const loose = tops.length - hats.length;
  if (loose > 0) warnings.push(t(loose === 1 ? 'blocks.problem.loose.one' : 'blocks.problem.loose.other', { n: loose }));

  const read = readClauses(hat, kind);
  if ('error' in read) return { rule: base, ok: false, warnings: [...warnings, read.error] };

  const action = str(hat, 'ACTION') as RuleBased['action'];
  if (!action) return { rule: base, ok: false, warnings: [...warnings, t('blocks.problem.noAction')] };

  if (kind === 'rb') {
    return { rule: { ...(base as RuleBased), action, clauses: read.clauses } as R, ok: true, warnings };
  }

  const pb = base as PatternBased;
  const aggregate = str(hat, 'AGG') as PatternBased['aggregate'];
  const unit = num(hat, 'UNIT');
  const win = num(hat, 'WIN');
  const next = {
    ...pb,
    action,
    scope: str(hat, 'SCOPE') as PatternBased['scope'],
    event: str(hat, 'EVENT') as PatternBased['event'],
    aggregate,
    threshold: num(hat, 'THR'),
    windowSeconds: Number.isFinite(unit) && Number.isFinite(win) ? win * unit : Number.NaN,
    filters: read.clauses,
  } as PatternBased;

  const aggField = str(hat, 'AGGFIELD');
  // `count` takes no aggregate field, and the KEY must go — not be set to undefined —
  // or every diff and every deep-equality test sees a change that is not one.
  if (aggregate === 'count' || !aggField) delete (next as { aggregateField?: unknown }).aggregateField;
  else next.aggregateField = aggField as FieldName;

  return { rule: next as R, ok: true, warnings };
}

/**
 * Should the workspace be reloaded from the rule? Only when the rule now says something
 * the canvas does not — otherwise the load would fight the user's own typing. Same idiom
 * as `ClauseBuilder`'s `emitted` ref (`components/ClauseBuilder.tsx:73-82`), lifted out so
 * the sync rule itself can be tested without a workspace.
 */
export function needsReload(rule: RuleBased | PatternBased, kind: RuleKindShort, lastSerialized: string | null): boolean {
  return JSON.stringify(ruleToWorkspaceState(rule, kind)) !== lastSerialized;
}

export function serializeRuleState(rule: RuleBased | PatternBased, kind: RuleKindShort): string {
  return JSON.stringify(ruleToWorkspaceState(rule, kind));
}
